import { type ReactNode } from 'react'

/**
 * Shared, intentionally-small UI primitives for the PWA settings panels.
 * Every control uses the same tokens as the rest of the PWA (bg-primary,
 * accent-primary, etc.) so the settings sheet inherits the current theme.
 *
 * Keep this file dumb — no state, no hooks. Panels hold the state.
 */

export function PanelCard({
  title,
  description,
  status,
  children,
}: {
  title: string
  description?: string
  status?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-lg border border-border-primary bg-bg-secondary p-4">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
          {description && (
            <p className="mt-0.5 text-[11px] text-text-tertiary">
              {description}
            </p>
          )}
        </div>
        {status}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  )
}

export function StatusPill({
  kind,
  label,
}: {
  kind: 'ok' | 'warn' | 'error' | 'idle' | 'loading'
  label: string
}) {
  const cls =
    kind === 'ok'
      ? 'bg-green-500/20 text-green-300'
      : kind === 'warn'
        ? 'bg-yellow-500/20 text-yellow-300'
        : kind === 'error'
          ? 'bg-red-500/20 text-red-300'
          : kind === 'loading'
            ? 'bg-blue-500/20 text-blue-300'
            : 'bg-bg-tertiary text-text-tertiary'
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  )
}

export function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <label className="block text-xs font-medium text-text-primary">
          {label}
        </label>
        {description && (
          <p className="mt-0.5 text-[11px] text-text-tertiary">{description}</p>
        )}
      </div>
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <div className="peer h-5 w-9 rounded-full bg-bg-tertiary after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-accent-primary peer-checked:after:translate-x-full peer-disabled:opacity-50"></div>
      </label>
    </div>
  )
}

export function SelectField<T extends string>({
  label,
  description,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string
  description?: string
  value: T
  options: { value: T; label: string }[]
  disabled?: boolean
  onChange: (value: T) => void
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-text-primary">
        {label}
      </label>
      {description && (
        <p className="mb-1.5 text-[11px] text-text-tertiary">{description}</p>
      )}
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full rounded-md border border-border-primary bg-bg-tertiary px-2.5 py-1.5 text-xs text-text-primary focus:border-accent-primary focus:outline-none disabled:opacity-50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function SliderField({
  label,
  description,
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  label: string
  description?: string
  value: number
  min: number
  max: number
  step: number
  disabled?: boolean
  onChange: (value: number) => void
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium text-text-primary">{label}</label>
        <span className="text-[11px] text-text-tertiary">
          {value.toFixed(2)}
        </span>
      </div>
      {description && (
        <p className="mb-1.5 text-[11px] text-text-tertiary">{description}</p>
      )}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-accent-primary disabled:opacity-50"
      />
    </div>
  )
}

export function TextField({
  label,
  description,
  value,
  placeholder,
  type = 'text',
  disabled,
  onChange,
  rightSlot,
}: {
  label: string
  description?: ReactNode
  value: string
  placeholder?: string
  type?: 'text' | 'password'
  disabled?: boolean
  onChange: (value: string) => void
  rightSlot?: ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-text-primary">
        {label}
      </label>
      <div className="flex gap-1.5">
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-md border border-border-primary bg-bg-tertiary px-2.5 py-1.5 text-xs text-text-primary focus:border-accent-primary focus:outline-none disabled:opacity-50"
        />
        {rightSlot}
      </div>
      {description && (
        <p className="mt-1 text-[11px] text-text-tertiary">{description}</p>
      )}
    </div>
  )
}
